import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, map, groupBy, mergeMap, toArray, catchError, throwError, retry, of, BehaviorSubject } from 'rxjs';
import { DateTime } from 'luxon';
import { WorkRequest } from 'src/app/types/work-request';
import { Task } from 'src/app/types/task';
import { Contact } from 'src/app/types/contact';
import { Sale, SalesOrOpportunitiesByCategory } from '../types/analytics';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
	providedIn: 'root'
})
export class DataService {
	public tenantId: number | null = null;
	private tenantIdSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);

	private _httpClient: HttpClient;
	private _authService: AuthService;

	constructor(authService: AuthService, httpClient: HttpClient) {
		this._authService = authService;
		this._httpClient = httpClient;

		this.getLookUpUserTenants().subscribe({
			next: (response: any) => {
				this.tenantId = response[0].id;
			}
		});
	}

	public selectTenant(tenantId: number): void {
		this.tenantIdSubject.next(tenantId);
	  }

	public getTenantId(): Observable<number> {
		return this.tenantIdSubject.asObservable();
	  }

	private handleError(error: HttpErrorResponse): Observable<any> {
        if (error.status === 406) {
			this._authService.reset();
			return of(null);
        }
	}

	public getLookUpUserTenants = (): Observable<any[]> =>
		this._httpClient.get<any>(`${environment.apiUrl}/LookUp/user/tenants`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public getLookUpUserCustomers = (): Observable<any[]> =>
		this._httpClient.get<any>(`${environment.apiUrl}/LookUp/user/customers`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);


	public getWorkRequests = (): Observable<WorkRequest[]> =>
		this._httpClient.get<any>(`${environment.apiUrl}/WorkRequest`).pipe(
 			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public getWorkRequestsByTenantId = (tenantId: number): Observable<any> =>
		this._httpClient.get<any>(`${environment.apiUrl}/WorkRequest/tenant/${tenantId}`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public getWorkRequest = (id: number): Observable<WorkRequest> =>
		this._httpClient.get<WorkRequest>(`${environment.apiUrl}/WorkRequest/${id}`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public postWorkRequest = (workRequest: WorkRequest): Observable<any> =>
		this._httpClient.post<any>(`${environment.apiUrl}/WorkRequest`, workRequest).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public putWorkRequest = (workRequest: WorkRequest): Observable<any> =>
		this._httpClient.put<any>(`${environment.apiUrl}/WorkRequest`, workRequest).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public deleteWorkRequest = (id: number): Observable<any> =>
		this._httpClient.delete<any>(`${environment.apiUrl}/WorkRequest/${id}`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public getWorkRequestAttachments = (id: number): Observable<any[]> =>
		this._httpClient.get<any[]>(`${environment.apiUrl}/FileStore/workrequest/${id}`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	public postWorkRequestAttachment = (id: number, file: File): Observable<any> => {
		if (!file || file.size === 0) {
			console.error('A fájl üres vagy nem érvényes.');
			return throwError(() => new Error('A fájl üres vagy nem érvényes.'));
		}
		return from(this.toBase64(file)).pipe(
			mergeMap(base64Content => {
				const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
				const data = {
					guid: crypto.randomUUID(),
					tenantId: this.tenantId,
					srcAvatarId: 114402,
					createDate: new Date(file.lastModified),
					name: nameWithoutExtension,
					originalName: nameWithoutExtension,
					originalDate: new Date(file.lastModified),
					fileExtension: file.name.split('.').pop(),
					fileContent: base64Content
				};
				return this._httpClient.post<any>(`${environment.apiUrl}/FileStore/workrequest/${id}`, data);
			})
			// }),
			// catchError(error => {
			// 	console.error('Hiba a fájl feltöltésekor:', error);
			// 	return throwError(() => error);
			// })
		);
	};

	public deleteWorkRequestAttachment = (id: number, fileStoreId: number): Observable<any> =>
		this._httpClient.delete<any>(`${environment.apiUrl}/FileStore/workrequest/${id}/${fileStoreId}`).pipe(
			retry(3),
//			catchError((error: HttpErrorResponse) => this.handleError(error))
		);

	private toBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			if (!file) {
				reject(new Error('No file provided.'));
				return;
			}
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const result = reader.result as string;
					if (!result) {
						reject(new Error('FileReader result is empty.'));
						return;
					}
					const base64 = result.split(',')[1];
					resolve(base64);
				} catch (err) {
					reject(err);
				}
			};
			reader.onerror = error => reject(error);
			reader.readAsDataURL(file);
		});
	}
	


	public getContacts = () =>
		this._httpClient.get<Contact[]>(`${environment.demoApiUrl}/Users/Contacts`);

	public getContact = (id: number) =>
		this._httpClient.get<Contact>(`${environment.demoApiUrl}/Users/Contacts/${id}`);

	public getTasks = (): Observable<Task[]> =>
		this._httpClient.get<Task[]>(`${environment.demoApiUrl}/Employees/AllTasks`);

	public getFilteredTasks = (): Observable<Task[]> =>
		this._httpClient.get<Task[]>(`${environment.demoApiUrl}/Employees/FilteredTasks`);

	public getTask = (id: number): Observable<Task> =>
		this._httpClient.get<Task>(`${environment.demoApiUrl}/Employees/Tasks/${id}`);

	public getContactNotes = (id: number) =>
		this._httpClient.get(`${environment.demoApiUrl}/Users/Contacts/${id}/Notes`);

	public getContactMessages = (id: number) =>
		this._httpClient.get(`${environment.demoApiUrl}/Users/Contacts/${id}/Messages`);

	public getActiveContactOpportunities = (id: number) =>
		this.getContactOpportunities(id, true);

	public getClosedContactOpportunities = (id: number) =>
		this.getContactOpportunities(id, false);

	public getContactOpportunities = (id: number, isActive: boolean) => this._httpClient
		.get<any>(`${environment.demoApiUrl}/Users/Contacts/${id}/Opportunities`)
		.pipe(
			map((data) => data.filter((_: any, index: number) => {
				const isEven = index % 2 === 0;
				return isActive ? isEven : !isEven;
			})),
		);

	public getSalesByStateAndCity = (startDate: string, endDate: string) => this._httpClient
		.get(`${environment.demoApiUrl}/Analytics/SalesByStateAndCity/${startDate}/${endDate}`);

	public getSalesByState = (data) => {
		let dataByState;
		from(data)
			.pipe(
				groupBy((s: any) => s.stateName),
				mergeMap((group) => group.pipe(toArray())),
				map((val) => {
					let total = 0;
					let percentage = 0;
					val.forEach((v) => {
						total = total + v.total;
						percentage = percentage + v.percentage;
					});

					return {
						stateName: val[0].stateName,
						stateCoords: val[0].stateCoords,
						total,
						percentage,
					};
				}),
				toArray(),
			).subscribe((data) => {
				dataByState = data;
			});

		return dataByState;
	};

	public getOpportunitiesByCategory = (startDate: string, endDate: string) => this._httpClient
		.get<SalesOrOpportunitiesByCategory>(`${environment.demoApiUrl}/Analytics/OpportunitiesByCategory/${startDate}/${endDate}`);

	public getSalesByCategory = (startDate: string, endDate: string) => this._httpClient
		.get<SalesOrOpportunitiesByCategory>(`${environment.demoApiUrl}/Analytics/SalesByCategory/${startDate}/${endDate}`);

	public getSalesByOrderDate = (groupByPeriod: string) => this._httpClient
		.get<Sale[]>(`${environment.demoApiUrl}/Analytics/SalesByOrderDate/${groupByPeriod}`);

	public getSales = (startDate: string, endDate: string) => this._httpClient
		.get<Sale[]>(`${environment.demoApiUrl}/Analytics/Sales/${startDate}/${endDate}`);

	public getProfile = (id: number) =>
		this._httpClient.get<Record<string, any>>(`${environment.demoApiUrl}/Users/Contacts/${id}`).pipe(
			map((data)=> {
				data.gender = id == 22 ? 'female' : null;
				data.birthDate = new Date('1980/05/03');
				data.hiredDate = new Date('2023/03/03');
				data.department = 'UI/UX';
				data.position = 'Designer';
				data.domainUsername = 'corp\\amelia.harper';
				data.country = 'USA';
				data.city = 'New York';
				data.state = 'New York';
				data.address = '405 E 42nd St';
				data.supervisor = 'Sam Adamson';
				return data;
			})
		);

	public getSupervisors = () => {
		const items = [];
		return from([items])
	};

	public getListDS = () => from([[
		{
			key: 'id',
			items: ['Brett Johnson', 'Tasks', 'Reminder', 'Contacts']
				.map((text) => ({ list: 'My Calendars', text }))
				.concat({ list: 'Other Calendars', text: 'Holidays' }),
		},
	]]);

	public getDefaultListDS = () => from([[
		{
			key: 'My Calendars',
			items: [
				{
					id: 0,
					text: 'Brett Johnson',
					color: '#B3E5FC',
					checkboxColor: '#29B6F6',
				},
				{
					id: 1,
					text: 'Tasks',
					color: '#C8E6C9',
					checkboxColor: '#66BB6A',
				},
				{
					id: 2,
					text: 'Reminder',
					color: '#FFCDD2',
					checkboxColor: '#EF5350',
				},
				{
					id: 3,
					text: 'Contacts',
					color: '#FFE0B2',
					checkboxColor: '#FFA726',
				}],
		},
		{
			key: 'Other Calendars',
			items: [{
				id: 4,
				text: 'Holidays',
				color: '#F3E5F5',
				checkboxColor: '#AB47BC',
			}],
		},
	]]);

	public getAppointmentsDefaultTime = (index) => [
		{
			weekDay: 0,
			weekIndex: 0,
			defaultTime: {
				hours: 7,
				minutes: 30,
			},
		},
		{
			weekDay: 1,
			weekIndex: 0,
			defaultTime: {
				hours: 10,
			},
		},
		{
			weekDay: 2,
			weekIndex: 0,
			defaultTime: {
				hours: 8,
				minutes: 30,
			},
		},
		{
			weekDay: 3,
			weekIndex: 0,
			defaultTime: {
				hours: 7,
			},
		},
		{
			weekDay: 0,
			weekIndex: 1,
			defaultTime: {
				hours: 9,
			},
		},
		{
			weekDay: 1,
			weekIndex: 1,
			defaultTime: {
				hours: 7,
			},
		},
		{
			weekDay: 2,
			weekIndex: 1,
			defaultTime: {
				hours: 8,
				minutes: 30,
			},
		},
		{
			weekDay: 3,
			weekIndex: 1,
			defaultTime: {
				hours: 9,
				minutes: 30,
			},
		},
		{
			weekDay: 1,
			weekIndex: 2,
			defaultTime: {
				hours: 8,
				minutes: 20,
			},
		},
		{
			weekDay: 2,
			weekIndex: 2,
			defaultTime: {
				hours: 9,
				minutes: 40,
			},
		},
		{
			weekDay: 3,
			weekIndex: 2,
			defaultTime: {
				hours: 8,
				minutes: 30,
			},
		},
	][index];

	public getSchedulerTasks = () => {
		const promptDescription = `The HtmlEditor component is a client-side WYSIWYG text editor.
The editor allows users to format text and integrate media elements into documents.
The result can be exported to HTML or Markdown.`;

		return this.getTasks().pipe(
			map((tasks) => {
				const today = DateTime.now();
				const mondayMidnight = today.set({
					weekday: 1, hour: 0, minute: 0, second: 0, millisecond: 0,
				});
				const uniqueTasks = tasks.slice(0, 11);

				return uniqueTasks.map((task, index) => {
					const { weekDay, weekIndex, defaultTime } = this.getAppointmentsDefaultTime(index);
					const taskStart = mondayMidnight.plus({
						weeks: weekIndex,
						days: weekDay,
					}).plus(defaultTime);
					return {
						...task,
						startDate: taskStart.toJSDate(),
						endDate: taskStart.plus({ hours: 3 }).toJSDate(),
						description: promptDescription,
						calendarId: weekDay,
					};
				});
			}))
	};
}
